// RavenDbClient.ts
/**
 * RavenDbClient
 *
 * A minimal browser‑compatible TypeScript wrapper around RavenDB’s HTTP API.
 * • Native fetch() + browser‐managed auth (cookies / client certs).  
 * • No external deps.  
 * • Documents must have an `id` field for saveDocument().
 */

interface RavenDbOptions {
    /** Base URL of your RavenDB server (no trailing slash) */
    url: string;
    /** Target database name */
    database: string;
}

interface RavenDocument {
    /**  
     * For auto‑ID: set to `"CollectionName/"`.  
     * For custom ID: set to `"CollectionName/123-A"`.  
     */
    id: string;
}
interface StaticIndexDefinition {
    name: string;
    maps: string[];
    reduce?: string;
}

interface QueryStats {
    totalResults: number;
    skippedResults: number;
    durationInMs: number;
    indexName?: string;
    isStale: boolean;
}

class RavenDbClient {
    private readonly baseUrl: string;
    private readonly dbName: string;

    constructor(options: RavenDbOptions) {
        this.baseUrl = options.url.replace(/\/$/, "");
        this.dbName = options.database;
    }

    private getHeaders(contentType = "application/json"): Headers {
        const headers = new Headers();
        if (contentType) 
            headers.set("Content-Type", contentType);
        return headers;
    }

    /**
     * Load a document *and* its change‑vector.
     * Injects `.id` and returns `{ document, changeVector }`.
     */
    async getDocument<T extends RavenDocument>(
        id: string
    ): Promise<{ document: T; changeVector: string }> {
        const url = `${this.baseUrl}/databases/${this.dbName}/docs?id=${encodeURIComponent(id)}`;
        const res = await fetch(url, {
            method: "GET",
            credentials: "include",
            mode: "cors",
            headers: this.getHeaders("") // no Content-Type on GET
        });
        if (!res.ok) {
            throw new Error(`Get document failed (${res.status}): ${await res.text()}`);
        }

        const payload = await res.json();
        const result = payload.Results?.[0];
        if (!result) {
            throw new Error(`Document not found: ${id}`);
        }

        const metadata = result["@metadata"] as { "@change-vector": string };
        const changeVector = metadata["@change-vector"];
        // strip Raven metadata, but preserve stored fields + id
        const { "@metadata": _, ...stored } = result as any;
        const doc = { ...(stored as T), id } as T;

        return { document: doc, changeVector };
    }

    /**
     * Save (create or update) a document using `doc.id`:
     * • If `doc.id` ends with `/`, RavenDB auto‑generates an ID.  
     * • Otherwise uses exactly `doc.id`.  
     * Optionally sends `If-Match` for optimistic concurrency.
     *
     * On auto‑ID, writes the returned ID back into `doc.id`.
     *
     * @param doc           Must implement `{ id: string }`.
     * @param changeVector? Optional vector from a prior getDocument().
     * @returns             The final document ID.
     */
    async saveDocument<T extends RavenDocument>(
        doc: T,
        changeVector?: string
    ): Promise<string> {
        const url = `${this.baseUrl}/databases/${this.dbName}/docs?id=${encodeURIComponent(
            doc.id
        )}`;
        const headers = this.getHeaders();
        if (changeVector) {
            headers.set("If-Match", changeVector);
        }

        const res = await fetch(url, {
            method: "PUT",
            credentials: "include",
            headers,
            mode: "cors",
            body: JSON.stringify(doc)
        });
        if (!res.ok) {
            throw new Error(`Save document failed (${res.status}): ${await res.text()}`);
        }

        const payload = await res.json();
        // payload.Id is the actual ID (especially for auto‑ID)
        doc.id = payload.Id;
        return payload.Id;
    }
    /**
     * Delete a document by its full ID.
     * @param id Full document ID, e.g. "users/1-A".
     */
    async deleteDocument(id: string): Promise<void> {
        const url = `${this.baseUrl}/databases/${this.dbName}/docs?id=${encodeURIComponent(id)}`;
        const res = await fetch(url, {
            method: "DELETE",
            credentials: "include",
            headers: this.getHeaders("") // no Content-Type on DELETE
        });
        if (!res.ok) {
            throw new Error(`Delete document failed (${res.status}): ${await res.text()}`);
        }
    }
    /**
     * Deploy one or more static indexes (map or map‑reduce).
     *
     * Uses the server’s PutIndexesOperation via
     * POST /databases/{db}/admin/indexes :contentReference[oaicite:1]{index=1}.
     *
     * To create a multi‑map index, supply multiple strings in `maps`.
     * (A single‐map index is just `maps: ["from …"]`.) :contentReference[oaicite:2]{index=2}
     */
    async createIndex(def: StaticIndexDefinition): Promise<void> {
        const url = `${this.baseUrl}/databases/${this.dbName}/admin/indexes`;
        const payload = {
            Indexes: [
                {
                    Name: def.name,
                    Maps: def.maps,
                    ...(def.reduce ? { Reduce: def.reduce } : {})
                }
            ]
        };
        const res = await fetch(url, {
            method: "POST",
            credentials: "include",
            headers: this.getHeaders(),
            mode: "cors",
            body: JSON.stringify(payload)
        });
        if (!res.ok) {
            throw new Error(`Create index failed (${res.status}): ${await res.text()}`);
        }
    }
    /**
     * Query the given collection.
     * @param collectionName name of your collection, e.g. "Users"
     * @param whereClause    RQL `where …` clause (no leading "where")
     * @param params         named parameters for `$` placeholders
     * @param skip           RQL `offset`
     * @param limit          RQL `limit`
     */
    async queryDocuments<T = any>(
        collectionName: string,
        whereClause: string,
        params: Record<string, any> = {},
        skip?: number,
        limit?: number
    ): Promise<{ results: T[]; stats: QueryStats }> {
        const rql = `from ${collectionName} where ${whereClause}`;
        return this._queryWithStats(rql, params, skip, limit);
    }

    /**
     * Query a named static or auto‑index.
     * @param indexName   name of your index, e.g. "Users/ByAge"
     * @param whereClause RQL `where …` clause (no leading "where")
     * @param params      named parameters for `$` placeholders
     * @param skip        RQL `offset`
     * @param limit       RQL `limit`
     */
    async queryIndex<T = any>(
        indexName: string,
        whereClause: string,
        params: Record<string, any> = {},
        skip?: number,
        limit?: number
    ): Promise<{ results: T[]; stats: QueryStats }> {
        const rql = `from index '${indexName}' where ${whereClause}`;
        return this._queryWithStats(rql, params, skip, limit);
    }

    /**
     * Run a paged query *and* get back the statistics in one call.
     *
     * Internally this POSTs to `/databases/{db}/queries` with:
     * {
     *   Query: "<your RQL>",
     *   QueryParameters: { … },
     *   Start: <skip>,         // optional
     *   PageSize: <limit>      // optional
     * }
     *
     * The response JSON contains:
     *   - Results: T[]
     *   - TotalResults: number
     *   - SkippedResults: number
     *   - DurationInMs: number
     *   - IndexName: string
     *   - IsStale: boolean
     *
     * @param rql    Full RQL, e.g. "from Users where Age > $min order by Name"
     * @param params Named parameters for `$` placeholders
     * @param skip   Optional number to skip (Start)
     * @param limit  Optional number to take  (PageSize)
     */
    async _queryWithStats<T = any>(
        rql: string,
        params: Record<string, any> = {},
        skip?: number,
        limit?: number
    ): Promise<{ results: T[]; stats: QueryStats }> {
        const url = `${this.baseUrl}/databases/${this.dbName}/queries`;
        const body: any = { Query: rql, QueryParameters: params };
        if (typeof skip === "number") body.Start = skip;
        if (typeof limit === "number") body.PageSize = limit;

        const res = await fetch(url, {
            method: "POST",
            credentials: "include",
            headers: this.getHeaders(),
            mode: "cors",
            body: JSON.stringify(body)
        });
        if (!res.ok) {
            throw new Error(`QueryWithStats failed (${res.status}): ${await res.text()}`);
        }

        const payload = await res.json();
        const results = payload.Results as T[];

        const stats: QueryStats = {
            totalResults: payload.TotalResults as number,
            skippedResults: payload.SkippedResults as number,
            durationInMs: payload.DurationInMs as number,
            indexName: payload.IndexName,
            isStale: payload.IsStale as boolean
        };
        return { results, stats };
    }
}
